<!--
  The structural sketch: what a 160px tile draws instead of text it cannot
  render.

  TASK-042 §5b. Grey bars for headings, a hatched block for code, a grid for a
  table. Not an excerpt -- our documents open with a title and a byline, so an
  excerpt previews as boilerplate and every tile in a folder shows the same
  three words. Not a rendered miniature either: that is a screenshot pipeline
  with a cache-invalidation problem attached. A shape is cheap, it is honest at
  a size where text is unreadable anyway, and it distinguishes documents by the
  one property that actually differs between them.

  **`aria-hidden` on the whole thing.** It is a shape, not information.
  Announcing "heading, paragraph, paragraph, table" for every tile is noise,
  and the tile's name and metadata already carry everything real (§9).

  The paragraph widths arrive bucketed to three by the server, deliberately, so
  that a sketch cannot be read back as a low-resolution copy of the text. This
  component must never be given an exact width to draw, however tempting: the
  bucketing is the privacy property, and it is only a property where it is
  computed.
-->
<script lang="ts">
  import type { Sketch } from './doclist-types'

  let { sketch }: { sketch: Sketch | null | undefined } = $props()

  /** Three widths and only three, matching the server's three buckets. */
  const WIDTH: Record<number, string> = { 1: '40%', 2: '70%', 3: '95%' }
</script>

<div class="sketch" aria-hidden="true">
  {#if !sketch}
    <!-- Not asked for yet, or nothing to draw. A neutral placeholder, because
         a tile must never wait on a preview. -->
    <div class="mark mark-blank"></div>
    <div class="mark mark-blank"></div>
  {:else}
    {#each sketch.marks as mark, i (i)}
      {#if mark.k === 'h'}
        <div class="mark bar bar-h" style={`width:${Math.max(30, 95 - (mark.level - 1) * 12)}%;`}></div>
      {:else if mark.k === 'p'}
        <div class="mark bar" style={`width:${WIDTH[mark.lines]}`}></div>
      {:else if mark.k === 'list'}
        <div class="mark list">
          {#each Array(mark.items) as _, n (n)}
            <span class="dot"></span><span class="bar bar-thin"></span>
          {/each}
        </div>
      {:else if mark.k === 'table'}
        <div
          class="mark table"
          style={`grid-template-columns:repeat(${Math.min(Math.max(mark.cols, 1), 6)},1fr);`}
        >
          {#each Array(Math.min(Math.max(mark.cols, 1), 6) * Math.min(Math.max(mark.rows, 1), 3)) as _, n (n)}
            <span class="cell"></span>
          {/each}
        </div>
      {:else if mark.k === 'code'}
        <div class="mark code"></div>
      {:else if mark.k === 'quote'}
        <div class="mark quote"><span class="bar bar-thin" style="width:75%"></span></div>
      {:else if mark.k === 'pending'}
        <div class="mark pending"></div>
      {:else}
        <div class="mark figure"></div>
      {/if}
    {/each}
    {#if sketch.truncated}
      <div class="mark more">⋯</div>
    {/if}
  {/if}
</div>

<style>
  .sketch {
    display: flex;
    flex-direction: column;
    gap: 0.28rem;
    height: 7rem;
    overflow: hidden;
    padding: 0.55rem;
    border-radius: 0.375rem 0.375rem 0 0;
    background: var(--color-paper, #faf9f5);
    /* The shape is drawn small and read at a glance; a hard edge against the
       tile below it is what makes it read as a page rather than as decoration. */
    border-bottom: 1px solid var(--color-line, #211d1824);
  }

  .mark {
    flex: 0 0 auto;
  }

  .bar {
    height: 0.3rem;
    border-radius: 999px;
    background: color-mix(in oklab, var(--color-ink-soft, #6e665c) 30%, transparent);
  }

  .bar-h {
    height: 0.45rem;
    background: color-mix(in oklab, var(--color-ink, #211d18) 45%, transparent);
  }

  .bar-thin {
    height: 0.22rem;
    flex: 1 1 auto;
  }

  .mark-blank {
    height: 0.3rem;
    width: 60%;
    border-radius: 999px;
    background: color-mix(in oklab, var(--color-ink-soft, #6e665c) 12%, transparent);
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
  }

  .list .bar-thin {
    display: block;
    width: 70%;
  }

  .dot {
    display: none;
  }

  .table {
    display: grid;
    gap: 1px;
    background: color-mix(in oklab, var(--color-ink-soft, #6e665c) 22%, transparent);
    padding: 1px;
    border-radius: 2px;
  }

  .cell {
    height: 0.3rem;
    background: var(--color-paper, #faf9f5);
  }

  .code {
    height: 1.1rem;
    border-radius: 2px;
    background: repeating-linear-gradient(
      45deg,
      color-mix(in oklab, var(--color-ink-soft, #6e665c) 22%, transparent),
      color-mix(in oklab, var(--color-ink-soft, #6e665c) 22%, transparent) 2px,
      transparent 2px,
      transparent 5px
    );
  }

  .quote {
    display: flex;
    padding-left: 0.3rem;
    border-left: 2px solid color-mix(in oklab, var(--color-ink-soft, #6e665c) 35%, transparent);
  }

  .figure {
    height: 1.2rem;
    border-radius: 2px;
    background: color-mix(in oklab, var(--color-ink-soft, #6e665c) 16%, transparent);
  }

  .pending {
    height: 0.6rem;
    border-radius: 2px;
    border: 1px dashed color-mix(in oklab, var(--color-red-deep, #c01030) 45%, transparent);
  }

  .more {
    font-size: 0.7rem;
    line-height: 1;
    color: var(--color-ink-soft, #6e665c);
  }
</style>
